import { db, OfflineOperation } from './offline-db';
import { supabase } from './supabase';
import { queryClient } from './react-query';
import { projectKeys } from '../hooks/queries/projectKeys';
import { topicKeys } from '../hooks/queries/topicKeys';
import { taskColumnKeys } from '../hooks/queries/taskColumnKeys';
import { taskKeys } from '../hooks/queries/taskKeys';
import { noteKeys } from '../hooks/queries/noteKeys';
import { logger } from '../utils/logger';
import { CLIENT_ID } from './client-context';

/**
 * Processes the queue of offline operations when the app comes back online
 * Handles syncing operations in the correct order (creates, updates, deletes)
 * @returns Promise that resolves when all operations are processed
 */
export async function processOfflineQueue(): Promise<{ 
  processed: number; 
  failed: number; 
  total: number 
}> {
  logger.sync('Starting offline queue processing');
  
  try {
    // Get all pending operations ordered by timestamp
    const operations = await db.offlineOperations
      .where('synced')
      .equals(0)
      .sortBy('timestamp');
      
    if (operations.length === 0) {
      logger.sync('No pending offline operations to process');
      return { processed: 0, failed: 0, total: 0 };
    }
    
    logger.sync(`Found ${operations.length} pending operations`, {
      tables: operations.map(op => op.table).filter((v, i, a) => a.indexOf(v) === i)
    });
    
    // Group operations by table for more efficient processing
    const operationsByTable = operations.reduce((acc, op) => {
      if (!acc[op.table]) acc[op.table] = [];
      acc[op.table].push(op);
      return acc;
    }, {} as Record<string, OfflineOperation[]>);
    
    // Process each table's operations
    const results = await Promise.all(
      Object.entries(operationsByTable).map(([table, tableOperations]) => 
        processTableOperations(table, tableOperations)
      )
    );
    
    // Calculate totals
    const totals = results.reduce(
      (acc, result) => ({
        processed: acc.processed + result.processed,
        failed: acc.failed + result.failed,
        total: acc.total + result.total
      }),
      { processed: 0, failed: 0, total: 0 }
    );
    
    // Invalidate relevant queries to refresh data
    if (totals.processed > 0) {
      logger.sync('Invalidating queries after offline sync');
      
      if (operationsByTable['projects']) {
        queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      }
      
      // Invalidate topics queries if topics were changed
      if (operationsByTable['topics']) {
        queryClient.invalidateQueries({ queryKey: topicKeys.lists() });
      }
      
      // Invalidate task columns queries if task columns were changed
      if (operationsByTable['taskColumns']) {
        queryClient.invalidateQueries({ queryKey: taskColumnKeys.lists() });
      }
      
      // Invalidate tasks queries if tasks were changed
      if (operationsByTable['tasks']) {
        queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      }
      
      // Invalidate notes queries if notes were changed
      if (operationsByTable['notes']) {
        queryClient.invalidateQueries({ queryKey: noteKeys.lists() });
      }
      
      // Add other table queries as they're implemented
    }
    
    logger.sync('Offline queue processing completed', totals);
    return totals;
  } catch (error) {
    logger.error('Error processing offline queue:', error);
    throw error;
  }
}

/**
 * Process operations for a specific table
 * Handles creates first, then updates, then deletes
 */
async function processTableOperations(
  table: string, 
  operations: OfflineOperation[]
): Promise<{ processed: number; failed: number; total: number }> {
  logger.sync(`Processing ${operations.length} operations for ${table}`);
  
  // Split operations by type
  const creates = operations.filter(op => op.operation === 'create');
  const updates = operations.filter(op => op.operation === 'update');
  const deletes = operations.filter(op => op.operation === 'delete');
  
  let processed = 0;
  let failed = 0;
  
  // Process creates first (as they may be referenced by later operations)
  for (const op of creates) {
    const success = await processOperation(table, op);
    if (success) processed++;
    else failed++;
  }
  
  // Then process updates
  for (const op of updates) {
    const success = await processOperation(table, op);
    if (success) processed++;
    else failed++;
  }
  
  // Finally process deletes
  for (const op of deletes) {
    const success = await processOperation(table, op);
    if (success) processed++;
    else failed++;
  }
  
  logger.sync(`Completed ${table} operations processing`, {
    total: operations.length,
    processed,
    failed
  });
  
  return { 
    processed, 
    failed, 
    total: operations.length 
  };
}

/**
 * Process a single operation to sync with server
 * @param table The database table
 * @param operation The operation to process
 * @returns Promise resolving to true if successful
 */
async function processOperation(
  table: string, 
  operation: OfflineOperation
): Promise<boolean> {
  try {
    // Add metadata to track client origin
    const operationWithMetadata = {
      ...operation.data,
      metadata: {
        ...(operation.data.metadata || {}),
        client_id: CLIENT_ID,
        synced_at: new Date().toISOString()
      }
    };
    
    // Process based on operation type
    switch (operation.operation) {
      case 'create': {
        const { data, error } = await supabase
          .from(table)
          .insert(operationWithMetadata)
          .select()
          .single();
          
        if (error) throw error;
        
        // Store server data in IndexedDB
        if (data) {
          await db[table].put(data);
        }
        
        // Mark operation as synced
        await db.offlineOperations
          .where('id')
          .equals(operation.id)
          .modify({ synced: 1 });
        
        return true;
      }
      
      case 'update': {
        const { data, error } = await supabase
          .from(table)
          .update(operationWithMetadata)
          .eq('id', operation.entity_id)
          .select()
          .single();
          
        if (error) throw error;
        
        // Store server data in IndexedDB
        if (data) {
          await db[table].put(data);
        }
        
        // Mark operation as synced
        await db.offlineOperations
          .where('id')
          .equals(operation.id)
          .modify({ synced: 1 });
        
        return true;
      }
      
      case 'delete': {
        const { error } = await supabase
          .from(table)
          .delete()
          .eq('id', operation.entity_id);
          
        if (error) throw error;
        
        // Remove from IndexedDB
        await db[table].delete(operation.entity_id);
        
        // Mark operation as synced
        await db.offlineOperations
          .where('id')
          .equals(operation.id)
          .modify({ synced: 1 });
        
        return true;
      }
      
      default:
        logger.error(`Unknown operation type: ${operation.operation}`);
        return false;
    }
  } catch (error) {
    logger.error(`Failed to sync ${operation.operation} operation for ${table}:`, error);
    
    // Mark as failed sync with error details
    await db.offlineOperations
      .where('id')
      .equals(operation.id)
      .modify({ 
        synced: -1,
        error: error instanceof Error ? error.message : String(error) 
      });
    
    return false;
  }
}

/**
 * Queues an operation to be processed when back online
 * For use when creating, updating, or deleting while offline
 */
export async function queueOfflineOperation(
  table: string,
  operation: OperationType,
  entityId: string,
  data: any
): Promise<string> {
  const operationId = crypto.randomUUID();
  const timestamp = new Date().toISOString();
  
  await db.offlineOperations.add({
    id: operationId,
    table,
    operation,
    entity_id: entityId,
    data,
    timestamp,
    synced: 0
  });
  
  logger.sync(`Queued ${operation} operation for ${table}`, { entityId });
  return operationId;
}

/**
 * Type for operations that can be queued while offline
 */
export type OperationType = 'create' | 'update' | 'delete';